import { buildSipUri, extractSipUsername } from './sipPagingZoneHelpers';

const TWILIO_USERNAME_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;

/** Accept a bare username or a pasted SIP URI / user@domain. */
export function extractTwilioSipUsername(value) {
  let raw = String(value || '').trim();
  if (!raw) return '';
  if (/^sip:/i.test(raw)) raw = raw.slice(4);
  if (raw.includes('@')) raw = raw.split('@')[0];
  return raw.trim();
}

export function suggestTwilioSipUsername(name) {
  return extractTwilioSipUsername(name)
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
}

export function isValidTwilioSipUsername(username) {
  return TWILIO_USERNAME_RE.test(suggestTwilioSipUsername(username));
}

function kindFor(credential, zone) {
  if (zone) return 'SIP Page Zone';
  const identity = String(credential?.identityType || credential?.deviceType || '').toLowerCase();
  if (identity === 'panel') return 'Panel';
  return 'SIP User';
}

/**
 * One row per Twilio SIP credential, plus paging zones that have no matching credential yet.
 */
export function buildSipUserRows({ credentials = [], zones = [], domain = '' } = {}) {
  const zoneByUser = new Map();
  (Array.isArray(zones) ? zones : []).forEach((zone) => {
    const username = extractSipUsername(zone?.sipUri || '').toLowerCase();
    if (!username || zoneByUser.has(username)) return;
    zoneByUser.set(username, zone);
  });

  const seen = new Set();
  const rows = [];

  (Array.isArray(credentials) ? credentials : []).forEach((cred) => {
    const username = String(cred?.username || '').trim();
    const key = username.toLowerCase();
    if (key) seen.add(key);
    const zone = key ? zoneByUser.get(key) : null;
    rows.push({
      key: String(cred?.credentialSid || cred?.id || `user-${username}`),
      username,
      label: String(cred?.label || zone?.name || zone?.label || username || 'SIP user'),
      kind: kindFor(cred, zone),
      sipUri: zone?.sipUri || buildSipUri(username, domain) || '',
      credentialSid: String(cred?.credentialSid || cred?.id || ''),
      zoneId: zone?.id || zone?.zoneId || null,
      createdAt: cred?.createdAt || null,
    });
  });

  (Array.isArray(zones) ? zones : []).forEach((zone) => {
    const username = extractSipUsername(zone?.sipUri || '');
    const key = username.toLowerCase();
    if (key && seen.has(key)) return;
    rows.push({
      key: `zone-${zone?.id || username || rows.length}`,
      username,
      label: String(zone?.name || zone?.label || username || 'SIP page zone'),
      kind: 'SIP Page Zone',
      sipUri: zone?.sipUri || '',
      credentialSid: '',
      zoneId: zone?.id || zone?.zoneId || null,
      createdAt: zone?.createdAt || null,
    });
  });

  rows.sort((a, b) => String(a.label).localeCompare(String(b.label)));
  return rows;
}
